import React from 'react';
import PropTypes from 'prop-types';
import {
  LuCalendarDays,
  LuCheck,
  LuCircleCheck,
  LuCircleX,
  LuClock,
  LuDownload,
  LuExternalLink,
  LuFileText,
  LuPencil,
  LuTag,
  LuTrash2,
  LuUser,
  LuX,
} from 'react-icons/lu';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import IconButton from '../ui/IconButton';
import { useTranslation } from '../../i18n/LanguageContext';
import { CARD } from '../../pages/ProfilePage/components/ProfileUi';
import { EXPENSE_STATUS_TONES, attachedPdfs, expenseCategoryLabel } from '../../pages/ProfilePage/expense/expenseOptions';

const STATUS_ICONS = { pending: LuClock, approved: LuCircleCheck, rejected: LuCircleX };

// Durumun rengi kartın sol kenarında da görünür
const EDGE_TONES = {
  pending: 'before:tw-bg-amber-400',
  approved: 'before:tw-bg-emerald-500',
  rejected: 'before:tw-bg-rose-500',
};

/** One expense claim: amount, who/what/when, documents and the actions allowed. */
const ExpenseRequestCard = ({ request, isUserAdmin, onApprove, onReject, onDelete, onViewReason, onEditReason, onPdfView, onPdfDownload }) => {
  const { t, locale } = useTranslation();
  const label = (key, vars) => t(`profilePage.expense.approvals.${key}`, vars);
  const status = request.status || 'pending';
  const StatusIcon = STATUS_ICONS[status] || LuClock;
  const pdfs = attachedPdfs(request);
  const pending = request.status === 'pending';

  return (
    <li
      className={`${CARD} tw-flex tw-flex-col tw-gap-3 tw-overflow-hidden tw-p-4 before:tw-absolute before:tw-inset-y-0 before:tw-left-0 before:tw-w-1 ${EDGE_TONES[status] || EDGE_TONES.pending}`}
    >
      <div className="tw-flex tw-items-start tw-justify-between tw-gap-3">
        <div className="tw-min-w-0 tw-space-y-1">
          <p className="tw-text-2xl tw-font-semibold tw-tracking-tight tw-tabular-nums tw-text-slate-900 dark:tw-text-white">
            ₺{request.amount?.toLocaleString(locale) || '0'}
          </p>
          <p className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-1 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
            {isUserAdmin && request.employeeName && (
              <span className="tw-inline-flex tw-min-w-0 tw-items-center tw-gap-1 tw-font-semibold tw-text-slate-700 dark:tw-text-slate-200">
                <LuUser aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-shrink-0" />
                <span className="tw-truncate">{request.employeeName}</span>
              </span>
            )}
            <span className="tw-inline-flex tw-items-center tw-gap-1">
              <LuTag aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
              {expenseCategoryLabel(t, request.category)}
            </span>
            <span className="tw-inline-flex tw-items-center tw-gap-1 tw-tabular-nums">
              <LuCalendarDays aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
              {request.createdAt
                ? new Date(request.createdAt).toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' })
                : label('noDate')}
            </span>
          </p>
        </div>
        <Badge tone={EXPENSE_STATUS_TONES[status] || 'warning'}>
          <StatusIcon aria-hidden="true" className="tw-h-3 tw-w-3" />
          {t(`profilePage.expense.status.${EXPENSE_STATUS_TONES[status] ? status : 'pending'}`)}
        </Badge>
      </div>

      {request.description && (
        <p className="tw-whitespace-pre-line tw-break-words tw-text-sm tw-leading-relaxed tw-text-slate-700 dark:tw-text-slate-200">{request.description}</p>
      )}

      {pdfs.length > 0 && (
        <div className="tw-space-y-1.5">
          <p className="tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-400 dark:tw-text-slate-500">
            {label('documents', { count: pdfs.length })}
          </p>
          <ul className="tw-space-y-1.5">
            {pdfs.map((pdf, index) => {
              const name = pdf.name || label('documentN', { n: index + 1 });
              return (
                <li key={`${pdf.url}-${index}`} className="tw-flex tw-items-center tw-gap-2 tw-rounded-xl tw-border tw-border-slate-200/80 tw-bg-slate-50/70 tw-py-1 tw-pl-2.5 tw-pr-1 dark:tw-border-white/10 dark:tw-bg-white/[0.03]">
                  <LuFileText aria-hidden="true" className="tw-h-4 tw-w-4 tw-shrink-0 tw-text-rose-500" />
                  <span className="tw-min-w-0 tw-flex-1 tw-truncate tw-text-xs tw-font-medium tw-text-slate-700 dark:tw-text-slate-200" title={name}>{name}</span>
                  <IconButton label={label('view', { name })} onClick={() => onPdfView(pdf.url, pdf.name)}>
                    <LuExternalLink aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
                  </IconButton>
                  <IconButton label={label('download', { name })} onClick={() => onPdfDownload(pdf.url, pdf.name)}>
                    <LuDownload aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
                  </IconButton>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {request.status === 'rejected' && request.rejectionReason && (
        <div className="tw-rounded-xl tw-bg-rose-500/10 tw-px-3.5 tw-py-2.5 tw-text-sm tw-text-rose-900 dark:tw-text-rose-100">
          <p className="tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-opacity-70">{label('rejectionReason')}</p>
          <p className="tw-mt-0.5 tw-line-clamp-3 tw-whitespace-pre-line tw-break-words">{request.rejectionReason}</p>
          <div className="tw-mt-1.5 tw-flex tw-flex-wrap tw-gap-1">
            <button
              type="button"
              onClick={() => onViewReason(request.rejectionReason)}
              className="tw-rounded-full tw-bg-transparent tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold tw-text-rose-700 hover:tw-bg-rose-500/10 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-rose-500/40 dark:tw-text-rose-200"
            >
              {label('readReason')}
            </button>
            {isUserAdmin && (
              <button
                type="button"
                onClick={() => onEditReason(request.id, request.rejectionReason)}
                className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-transparent tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold tw-text-rose-700 hover:tw-bg-rose-500/10 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-rose-500/40 dark:tw-text-rose-200"
              >
                <LuPencil aria-hidden="true" className="tw-h-3 tw-w-3" />
                {label('editReason')}
              </button>
            )}
          </div>
        </div>
      )}

      {(pending || (request.status !== 'pending' && request.updatedAt)) && (
        <div className="tw-mt-auto tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2 tw-border-t tw-border-slate-200/70 tw-pt-3 dark:tw-border-white/10">
          <span className="tw-text-xs tw-text-slate-400 dark:tw-text-slate-500">
            {!pending && request.updatedAt
              ? label('processedOn', { date: new Date(request.updatedAt).toLocaleString(locale, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) })
              : ''}
          </span>
          {isUserAdmin && pending && (
            <div className="tw-flex tw-w-full tw-gap-2 sm:tw-w-auto">
              <Button size="sm" variant="danger" onClick={onReject} className="max-sm:tw-flex-1">
                <LuX aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
                {label('reject')}
              </Button>
              <Button size="sm" variant="success" onClick={onApprove} className="max-sm:tw-flex-1">
                <LuCheck aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
                {label('approve')}
              </Button>
            </div>
          )}
          {!isUserAdmin && pending && (
            <Button size="sm" variant="danger" onClick={onDelete}>
              <LuTrash2 aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
              {label('delete')}
            </Button>
          )}
        </div>
      )}
    </li>
  );
};

ExpenseRequestCard.propTypes = {
  request: PropTypes.object.isRequired,
  isUserAdmin: PropTypes.bool,
  onApprove: PropTypes.func.isRequired,
  onReject: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onViewReason: PropTypes.func.isRequired,
  onEditReason: PropTypes.func.isRequired,
  onPdfView: PropTypes.func.isRequired,
  onPdfDownload: PropTypes.func.isRequired,
};

export default ExpenseRequestCard;
